import { useQuery } from "@tanstack/react-query";
import { Briefcase, Car, HeartPulse, Home, KeyRound, Shield, Truck, Umbrella } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { fetchPublicConfig, submitIntake, type IntakeSubmission } from "../api";
import { PoweredByStatic } from "../components/PoweredBy";
import { translate, type Locale } from "../i18n";
import { applyBrand } from "../theme";

type Line = IntakeSubmission["line"];
const LINES: Array<{ id: Line; icon: typeof Car }> = [
  { id: "auto", icon: Car }, { id: "home", icon: Home }, { id: "renters", icon: KeyRound }, { id: "commercial_auto", icon: Truck },
  { id: "general_liability", icon: Shield }, { id: "bop", icon: Briefcase }, { id: "life", icon: Umbrella }, { id: "health", icon: HeartPulse },
];

/** Public website intake. No app chrome, no auth headers, bilingual, honeypot. Requests land in the Sales pipeline with consent recorded. */
export function IntakeScreen() {
  const cfg = useQuery({ queryKey: ["publicConfig"], queryFn: fetchPublicConfig, staleTime: 300_000 });
  const [locale, setLocale] = useState<Locale>(() => (navigator.language.toLowerCase().startsWith("es") ? "es" : "en"));
  const t = (k: string) => translate(locale, k);
  const [form, setForm] = useState({ firstName: "", lastName: "", phone: "", email: "", line: "auto" as Line, notes: "", smsConsent: false, website: "" });
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (cfg.data) {
      applyBrand(cfg.data.agency.brand);
      document.title = `${cfg.data.agency.displayName} · ${t("intake.title")}`;
      document.documentElement.lang = locale;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.data, locale]);

  const agency = cfg.data?.agency;
  const lines = LINES.filter((l) => !agency?.lines?.length || agency.lines.includes(l.id));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim() || (!form.phone.trim() && !form.email.trim())) {
      setError(t("intake.required"));
      return;
    }
    setError("");
    setState("sending");
    const res = await submitIntake({
      firstName: form.firstName.trim(), lastName: form.lastName.trim(), phone: form.phone.trim() || undefined, email: form.email.trim() || undefined,
      line: form.line, locale, notes: form.notes.trim() || undefined, smsConsent: form.smsConsent, consentText: t("intake.consent.sms"), website: form.website,
    });
    if (res.ok) setState("done");
    else {
      setState("error");
      setError(res.error);
    }
  };

  const field = "w-full min-h-[52px] rounded-2xl border border-ink/15 bg-white px-4 text-lg text-ink placeholder:text-ink/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";

  return (
    <div className="min-h-screen bg-surface text-ink font-body">
      <header className="bg-ink text-white">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            {agency ? <img src={agency.brand.logoPath} alt={agency.displayName} className="h-10 w-auto rounded bg-white p-1" /> : null}
            <span className="heading text-lg font-bold">{agency?.displayName ?? ""}</span>
          </div>
          <div className="flex rounded-xl border border-white/30 overflow-hidden" role="group" aria-label="Language">
            {(agency?.supportedLocales ?? ["en", "es"]).map((l) => (
              <button key={l} onClick={() => setLocale(l)} aria-pressed={locale === l} className={`min-h-[44px] px-4 text-sm font-bold ${locale === l ? "bg-white text-ink" : "text-white/80"}`}>{l.toUpperCase()}</button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6">
        {state === "done" ? (
          <section className="rounded-3xl bg-white p-8 text-center shadow-card">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/15 text-primary-dark flex items-center justify-center text-3xl">✓</div>
            <h1 className="text-3xl font-bold">{t("intake.thanks")}</h1>
            <p className="mt-2 text-ink/70">{t("intake.thanksBody")}</p>
            {agency ? <a href={`tel:${agency.contact.phone.replace(/\D/g, "")}`} className="mt-4 inline-flex min-h-[52px] items-center justify-center rounded-2xl bg-primary px-6 text-lg font-bold text-white">{agency.contact.phone}</a> : null}
            <button className="mt-6 block mx-auto min-h-[44px] text-sm font-semibold text-primary-dark underline" onClick={() => { setForm({ firstName: "", lastName: "", phone: "", email: "", line: "auto", notes: "", smsConsent: false, website: "" }); setState("idle"); }}>{t("intake.another")}</button>
          </section>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <h1 className="text-3xl font-bold">{t("intake.title")}</h1>
              <p className="text-ink/70 text-lg">{agency?.tagline[locale] ? `${agency.tagline[locale]}. ` : ""}{t("intake.subtitle")}</p>
            </div>

            <fieldset>
              <legend className="mb-2 text-sm font-bold text-ink/80">{t("intake.line")}</legend>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {lines.map((l) => (
                  <button key={l.id} type="button" onClick={() => setForm({ ...form, line: l.id })} aria-pressed={form.line === l.id}
                    className={`flex min-h-[72px] flex-col items-center justify-center gap-1 rounded-2xl border-2 text-sm font-bold ${form.line === l.id ? "border-primary bg-primary/10 text-primary-dark" : "border-ink/10 bg-white text-ink/80"}`}>
                    <l.icon className="h-6 w-6" />{t(`intake.line.${l.id}`)}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block"><span className="mb-1 block text-sm font-bold text-ink/80">{t("intake.firstName")}</span><input className={field} autoComplete="given-name" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required /></label>
              <label className="block"><span className="mb-1 block text-sm font-bold text-ink/80">{t("intake.lastName")}</span><input className={field} autoComplete="family-name" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} required /></label>
              <label className="block"><span className="mb-1 block text-sm font-bold text-ink/80">{t("intake.phone")}</span><input className={field} type="tel" inputMode="tel" autoComplete="tel" placeholder="(915) 555-0100" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
              <label className="block"><span className="mb-1 block text-sm font-bold text-ink/80">{t("intake.email")}</span><input className={field} type="email" inputMode="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
            </div>
            <label className="block"><span className="mb-1 block text-sm font-bold text-ink/80">{t("intake.notes")}</span><textarea className={`${field} min-h-[96px] py-3`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>

            {/* Honeypot: real people never see or fill this. */}
            <div className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden" aria-hidden="true">
              <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></label>
            </div>

            <label className="flex items-start gap-3 rounded-2xl bg-white p-4 border border-ink/10 cursor-pointer">
              <input type="checkbox" className="mt-1 h-6 w-6 shrink-0 accent-[var(--brand-primary)]" checked={form.smsConsent} onChange={(e) => setForm({ ...form, smsConsent: e.target.checked })} />
              <span className="text-sm text-ink/80">{t("intake.consent.sms")}</span>
            </label>

            {error ? <p className="rounded-2xl bg-red-50 border border-red-200 p-3 text-red-800 font-semibold" role="alert">{error}</p> : null}

            <button type="submit" disabled={state === "sending"} className="w-full min-h-[56px] rounded-2xl bg-primary text-lg font-bold text-white shadow-sm hover:bg-primary-dark disabled:opacity-60">
              {state === "sending" ? t("intake.sending") : t("intake.submit")}
            </button>
            <p className="text-center text-xs text-ink/55">{t("intake.privacy")}</p>
          </form>
        )}
      </main>

      {agency ? (
        <footer className="mx-auto max-w-2xl px-4 py-6 text-center text-xs text-ink/55">
          {agency.offices.map((o) => <p key={o.name}>{o.name} · {o.address} · {o.phone}</p>)}
          <PoweredByStatic locale={locale} className="mt-3" />
        </footer>
      ) : null}
    </div>
  );
}
